import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Save, Trash2 } from "lucide-react";

import { ApiError } from "@/lib/api/client";
import {
  useClinicalTeam,
  useClinicianProducts,
  useSaveClinicalAssignmentRules,
  useUpdateClinicalTeamMember,
} from "@/lib/api/clinicianHooks";
import { useBootstrap } from "@/lib/api/hooks";
import type {
  ClinicalAssignmentRule,
  ClinicalTeamMember,
} from "@/types/clinician";
import {
  ALL_STATE_CODES,
  IdentityIssueChips,
  parseStateCodes,
  PrescriberIdentityEditor,
} from "./PrescriberIdentityEditor";
import { PrescriberRotation } from "./PrescriberRotation";
import { SyncVisitStates } from "./SyncVisitStates";
import { PrescriberCoverage } from "./PrescriberCoverage";
import { globalFallbackCount } from "./assignmentRules";

const inputClass =
  "w-full rounded border border-line bg-page px-10 py-8 type-body-sm focus-visible:outline-none focus-visible:shadow-focus";

export function ClinicalTeamScreen() {
  const { data: bootstrap } = useBootstrap();
  const supervisor =
    bootstrap?.admin_role === "admin" ||
    bootstrap?.admin_role === "clinical_supervisor";
  const fullAdmin = bootstrap?.admin_role === "admin";
  const team = useClinicalTeam(supervisor);
  const products = useClinicianProducts();
  const saveRules = useSaveClinicalAssignmentRules();
  const [rules, setRules] = useState<ClinicalAssignmentRule[]>([]);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Turning routing on with existing unassigned patients needs a choice of
  // who keeps them, so they never enter the round-robin by accident.
  const [existingPatients, setExistingPatients] = useState<string | null>(null);
  const [keepWith, setKeepWith] = useState("");

  useEffect(() => {
    if (!dirty && team.data) {
      setRules(
        team.data.rules.map((rule) => ({
          ...rule,
          members: [...rule.members],
        })),
      );
    }
  }, [dirty, team.data]);

  if (!supervisor) {
    return (
      <main className="p-24 lg:p-32">
        <h1 className="type-heading-xl">Clinical team</h1>
        <p className="mt-8 type-body-sm text-ink-muted">
          Clinical supervisor permissions are required to manage provider
          routing.
        </p>
      </main>
    );
  }

  const clinicians = team.data?.clinicians ?? [];
  const globalCount = globalFallbackCount(rules);

  return (
    <main className="mx-auto max-w-[1320px] p-20 lg:p-32 space-y-24">
      <header className="flex flex-wrap items-end justify-between gap-12">
        <div>
          <p className="type-label-xs uppercase tracking-wider text-ink-muted">
            Supervisor settings
          </p>
          <h1 className="type-heading-xl mt-4">Clinical team</h1>
          <p className="type-body-sm text-ink-muted mt-6 max-w-[760px]">
            Map each portal clinician to their Healthie provider, control
            focused email alerts, and distribute new patients with weighted
            routing.
          </p>
        </div>
        <span className="rounded border border-line px-9 py-5 type-label-xs uppercase tracking-wide">
          {team.data?.team_mode_enabled
            ? "Routing active"
            : "Single-clinician mode"}
        </span>
      </header>

      {team.isError && (
        <p className="rounded border border-danger/30 bg-danger/5 p-12 type-body-sm text-danger">
          Clinical team settings could not be loaded.
        </p>
      )}

      <PrescriberCoverage enabled={supervisor} />

      <section className="rounded-lg border border-line bg-raised overflow-hidden">
        <div className="border-b border-line p-16">
          <h2 className="type-heading-md">Clinicians</h2>
          <p className="mt-4 type-body-sm text-ink-muted">
            Activation requires a Healthie provider ID. An active provider can
            prescribe or coordinate patients according to their capability.
            “Accepting” applies only to new primary-prescriber assignments.
          </p>
        </div>
        {team.isLoading ? (
          <p className="p-20 type-body-sm text-ink-muted">Loading team…</p>
        ) : (
          <div className="divide-y divide-line-subtle">
            {clinicians.map((member) => (
              <TeamMemberRow
                key={member.clinician_id}
                member={member}
                clinicians={clinicians}
                fullAdmin={fullAdmin}
              />
            ))}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-line bg-raised overflow-hidden">
        <div className="border-b border-line p-16 flex flex-wrap items-start justify-between gap-12">
          <div>
            <h2 className="type-heading-md">Patient distribution</h2>
            <p className="mt-4 type-body-sm text-ink-muted max-w-[760px]">
              Product rules run first; one global fallback is required. Weights
              are relative—for example, 70 and 30 produce a 70/30 split.
              Existing patients keep their current owner.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setRules((current) => [
                ...current,
                {
                  name:
                    globalCount === 0 ? "All other patients" : "Product rule",
                  priority: (current.length + 1) * 10,
                  active: true,
                  product_ids: [],
                  members: clinicians
                    .filter(
                      (member) =>
                        member.active && member.provider_type === "prescriber",
                    )
                    .map((member) => ({
                      clinician_id: member.clinician_id,
                      weight: 1,
                    })),
                },
              ]);
              setDirty(true);
            }}
            className="inline-flex items-center gap-6 rounded border border-line px-10 py-7 type-label-sm hover:bg-page"
          >
            <Plus size={14} /> Add rule
          </button>
        </div>

        <div className="divide-y divide-line-subtle">
          {rules.map((rule, index) => (
            <RuleEditor
              key={rule.id ?? `new-${index}`}
              rule={rule}
              clinicians={clinicians}
              products={products.data ?? []}
              onChange={(next) => {
                setRules((current) =>
                  current.map((item, itemIndex) =>
                    itemIndex === index ? next : item,
                  ),
                );
                setDirty(true);
              }}
              onRemove={() => {
                setRules((current) =>
                  current.filter((_, itemIndex) => itemIndex !== index),
                );
                setDirty(true);
              }}
            />
          ))}
          {!rules.length && (
            <p className="p-20 type-body-sm text-ink-muted">
              No routing rules yet. Until a complete rule set is saved, the
              portal keeps its existing single-clinician behavior.
            </p>
          )}
        </div>

        <div className="border-t border-line p-16 flex flex-wrap items-center justify-between gap-12">
          <p
            className={`type-body-sm ${globalCount === 1 ? "text-ink-muted" : "text-danger"}`}
          >
            {globalCount === 1
              ? "One active global fallback is configured."
              : `Exactly one active global fallback is required; found ${globalCount}.`}
          </p>
          <button
            type="button"
            disabled={
              !dirty ||
              saveRules.isPending ||
              (rules.length > 0 && globalCount !== 1)
            }
            onClick={async () => {
              setNotice(null);
              try {
                await saveRules.mutateAsync({ rules });
                setDirty(false);
                setNotice(
                  "Routing rules saved. New patients will use this distribution.",
                );
              } catch (error) {
                if (
                  error instanceof ApiError &&
                  error.code === "existing_patients_unassigned"
                ) {
                  setExistingPatients(error.message);
                  setKeepWith(defaultKeeper(clinicians));
                  return;
                }
                setNotice(`Rules were not saved: ${String(error)}`);
              }
            }}
            className="inline-flex items-center gap-6 rounded bg-ink px-14 py-8 type-label-sm text-ink-onAccent disabled:opacity-40"
          >
            <Save size={14} />{" "}
            {saveRules.isPending ? "Saving…" : "Save routing"}
          </button>
          {notice && (
            <p className="basis-full type-body-sm text-ink-muted">{notice}</p>
          )}
          {existingPatients && (
            <div className="basis-full rounded border border-warning/40 bg-warning/5 p-12 flex flex-col gap-8">
              <p className="type-body-sm">{existingPatients}</p>
              <p className="type-body-sm text-ink-muted">
                Existing patients stay with the prescriber already treating
                them. Only new patients are distributed by these rules.
              </p>
              <div className="flex flex-wrap items-center gap-8">
                <label className="type-label-sm">
                  Keep existing patients with
                  <select
                    className={`${inputClass} mt-5`}
                    value={keepWith}
                    onChange={(event) => setKeepWith(event.target.value)}
                  >
                    <option value="">Choose a prescriber</option>
                    {clinicians
                      .filter(
                        (member) =>
                          member.active &&
                          member.provider_type === "prescriber",
                      )
                      .map((member) => (
                        <option
                          key={member.clinician_id}
                          value={member.clinician_id}
                        >
                          {member.display_name || member.email}
                        </option>
                      ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={!keepWith || saveRules.isPending}
                  onClick={async () => {
                    setNotice(null);
                    try {
                      await saveRules.mutateAsync({
                        rules,
                        keepExistingPatientsWith: keepWith,
                      });
                      setDirty(false);
                      setExistingPatients(null);
                      setNotice(
                        "Routing is on. Existing patients kept their prescriber; new patients use this distribution.",
                      );
                    } catch (error) {
                      setNotice(`Rules were not saved: ${String(error)}`);
                    }
                  }}
                  className="self-end rounded bg-ink px-12 py-8 type-label-sm text-ink-onAccent disabled:opacity-40"
                >
                  Keep them and turn on routing
                </button>
                <button
                  type="button"
                  onClick={() => setExistingPatients(null)}
                  className="self-end rounded border border-line px-10 py-7 type-label-sm hover:bg-page"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <SyncVisitStates enabled={supervisor} />
    </main>
  );
}

/** The prescriber who keeps existing patients by default: the one still on
 *  the default script identity, else the only active prescriber. */
function defaultKeeper(clinicians: ClinicalTeamMember[]): string {
  const prescribers = clinicians.filter(
    (member) => member.active && member.provider_type === "prescriber",
  );
  const legacy = prescribers.find((member) => member.uses_legacy_identity);
  if (legacy) return legacy.clinician_id;
  return prescribers.length === 1 ? prescribers[0].clinician_id : "";
}

function TeamMemberRow({
  member,
  clinicians,
  fullAdmin,
}: {
  member: ClinicalTeamMember;
  clinicians: ClinicalTeamMember[];
  fullAdmin: boolean;
}) {
  const update = useUpdateClinicalTeamMember();
  const [panel, setPanel] = useState<
    "profile" | "identity" | "rotation" | null
  >(null);
  const editing = panel === "profile";
  const [form, setForm] = useState(member);
  const [statesText, setStatesText] = useState(
    member.licensed_states.join(", "),
  );
  const [error, setError] = useState<string | null>(null);
  const prescriber = member.provider_type === "prescriber";
  // Admins without a clinical profile list as inactive prescribers; script
  // identity only matters once someone is actually set up to prescribe.
  const onboarded =
    prescriber &&
    (member.active ||
      Boolean(member.healthie_provider_id) ||
      Boolean(member.prescriber_name) ||
      member.assigned_patient_count > 0);
  const invalidStates = parseStateCodes(statesText).invalid;

  useEffect(() => {
    setForm(member);
    setStatesText(member.licensed_states.join(", "));
  }, [member]);
  const toggle = (next: NonNullable<typeof panel>) =>
    setPanel((current) => (current === next ? null : next));

  return (
    <div className="p-16">
      <div className="flex flex-wrap items-center justify-between gap-12">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-7">
            <p className="type-body-sm font-medium truncate">
              {member.display_name || member.email || member.clinician_id}
            </p>
            <span
              className={`rounded px-6 py-2 type-label-xs uppercase ${member.active ? "bg-success/10 text-success" : "bg-sunken text-ink-muted"}`}
            >
              {member.active ? "Active" : "Inactive"}
            </span>
            {member.accepting_new_patients && (
              <span className="rounded bg-accent/10 px-6 py-2 type-label-xs uppercase text-accent">
                Accepting
              </span>
            )}
            <span className="rounded bg-sunken px-6 py-2 type-label-xs uppercase text-ink-muted">
              {member.provider_type}
            </span>
            {member.team_patient_scope && (
              <span className="rounded bg-info/10 px-6 py-2 type-label-xs uppercase text-info">
                Team patient scope
              </span>
            )}
            {member.healthie_provider_id && (
              <span
                className={`rounded px-6 py-2 type-label-xs uppercase ${member.credential_configured ? "bg-success/10 text-success" : "bg-warning/15 text-warning"}`}
              >
                {member.credential_configured
                  ? "Healthie ready"
                  : "Healthie key needed"}
              </span>
            )}
          </div>
          <p className="mt-3 type-label-xs text-ink-muted">
            {member.email} · {member.assigned_patient_count} assigned patients ·{" "}
            {member.notification_mode.replace("_", " ")} email
            {prescriber &&
              ` · ${
                member.licensed_states.length
                  ? `licensed in ${member.licensed_states.length} states`
                  : "no state limits recorded"
              }`}
          </p>
          {onboarded && (
            <div className="mt-5 flex flex-col gap-5">
              {(member.prescriber_name || member.uses_legacy_identity) && (
                <p className="type-label-xs text-ink-muted">
                  {member.prescriber_name
                    ? `Scripts: ${member.prescriber_name}${member.prescriber_npi ? ` · NPI ${member.prescriber_npi}` : ""}`
                    : "Scripts: configured default prescriber"}
                </p>
              )}
              <IdentityIssueChips issues={member.identity_issues ?? []} />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-6">
          {onboarded && (
            <button
              type="button"
              aria-expanded={panel === "identity"}
              onClick={() => toggle("identity")}
              className="rounded border border-line px-9 py-6 type-label-sm hover:bg-page"
            >
              {panel === "identity" ? "Close identity" : "Script identity"}
            </button>
          )}
          {onboarded && member.assigned_patient_count > 0 && (
            <button
              type="button"
              aria-expanded={panel === "rotation"}
              onClick={() => toggle("rotation")}
              className="rounded border border-line px-9 py-6 type-label-sm hover:bg-page"
            >
              {panel === "rotation" ? "Close" : "Move patients"}
            </button>
          )}
          <button
            type="button"
            onClick={() => toggle("profile")}
            className="inline-flex items-center gap-5 rounded border border-line px-9 py-6 type-label-sm hover:bg-page"
          >
            {editing ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {editing ? "Close" : "Edit"}
          </button>
        </div>
      </div>

      {panel === "identity" && (
        <div className="mt-16 border-t border-line-subtle pt-16">
          <PrescriberIdentityEditor member={member} clinicians={clinicians} />
        </div>
      )}
      {panel === "rotation" && (
        <div className="mt-16 border-t border-line-subtle pt-16">
          <PrescriberRotation member={member} fullAdmin={fullAdmin} />
        </div>
      )}

      {editing && (
        <div className="mt-16 grid gap-14 border-t border-line-subtle pt-16 lg:grid-cols-3">
          <label className="type-label-sm">
            Healthie provider ID
            <input
              className={`${inputClass} mt-5`}
              value={form.healthie_provider_id ?? ""}
              onChange={(event) =>
                setForm({ ...form, healthie_provider_id: event.target.value })
              }
              placeholder="Healthie user ID"
            />
          </label>
          <label className="type-label-sm">
            Clinical role
            <select
              className={`${inputClass} mt-5`}
              value={form.provider_type}
              onChange={(event) =>
                setForm({
                  ...form,
                  provider_type: event.target
                    .value as ClinicalTeamMember["provider_type"],
                  accepting_new_patients:
                    event.target.value === "nurse"
                      ? false
                      : form.accepting_new_patients,
                })
              }
            >
              <option value="prescriber">Prescriber</option>
              <option value="nurse">Nurse / care coordinator</option>
            </select>
          </label>
          <label className="type-label-sm">
            Timezone
            <input
              className={`${inputClass} mt-5`}
              value={form.timezone}
              onChange={(event) =>
                setForm({ ...form, timezone: event.target.value })
              }
            />
          </label>
          <label className="type-label-sm lg:col-span-3">
            Licensed states
            <span className="ml-8 inline-flex gap-6 align-middle">
              <button
                type="button"
                onClick={() => {
                  setStatesText(ALL_STATE_CODES.join(", "));
                  setForm({ ...form, licensed_states: [...ALL_STATE_CODES] });
                }}
                className="rounded border border-line px-6 py-2 type-label-xs hover:bg-page"
              >
                All 50 + DC
              </button>
              <button
                type="button"
                onClick={() => {
                  setStatesText("");
                  setForm({ ...form, licensed_states: [] });
                }}
                className="rounded border border-line px-6 py-2 type-label-xs hover:bg-page"
              >
                Clear
              </button>
            </span>
            <textarea
              className={`${inputClass} mt-5 min-h-[4.5rem]`}
              value={statesText}
              onChange={(event) => {
                setStatesText(event.target.value);
                setForm({
                  ...form,
                  licensed_states: parseStateCodes(event.target.value).states,
                });
              }}
              placeholder="CA, NY, TX"
            />
            <span
              className={`mt-3 block type-label-xs ${invalidStates.length ? "text-danger" : "text-ink-muted"}`}
            >
              {invalidStates.length
                ? `Not state codes: ${invalidStates.join(", ")}`
                : form.licensed_states.length
                  ? `${form.licensed_states.length} states. Patients elsewhere are never routed to this prescriber, and they can't approve them.`
                  : "Empty means no state limits — only for the existing default prescriber whose states were never recorded."}
            </span>
          </label>
          <label className="type-label-sm">
            Email cadence
            <select
              className={`${inputClass} mt-5`}
              value={form.notification_mode}
              onChange={(event) =>
                setForm({
                  ...form,
                  notification_mode: event.target
                    .value as ClinicalTeamMember["notification_mode"],
                })
              }
            >
              <option value="daily_digest">Daily digest (recommended)</option>
              <option value="immediate">Immediate</option>
              <option value="off">Off</option>
            </select>
          </label>
          <div className="lg:col-span-3 flex flex-wrap gap-16 type-body-sm">
            {[
              ["active", "Active clinician"],
              ["accepting_new_patients", "Accepting new patients"],
              ["team_patient_scope", "Coordinate all team patients"],
              ["notify_clinical_messages", "Clinical-message email"],
              ["notify_appointments", "Appointment email"],
            ].map(([field, label]) => (
              <label key={field} className="inline-flex items-center gap-6">
                <input
                  type="checkbox"
                  disabled={
                    (field === "accepting_new_patients" &&
                      form.provider_type === "nurse") ||
                    (field === "team_patient_scope" &&
                      form.provider_type !== "nurse")
                  }
                  checked={Boolean(form[field as keyof ClinicalTeamMember])}
                  onChange={(event) =>
                    setForm({ ...form, [field]: event.target.checked })
                  }
                />
                {label}
              </label>
            ))}
          </div>
          <div className="lg:col-span-3 flex items-center gap-10">
            <button
              type="button"
              disabled={update.isPending || invalidStates.length > 0}
              onClick={async () => {
                setError(null);
                try {
                  await update.mutateAsync({
                    clinicianId: member.clinician_id,
                    healthie_provider_id: form.healthie_provider_id,
                    active: form.active,
                    provider_type: form.provider_type,
                    team_patient_scope: form.team_patient_scope,
                    licensed_states: form.licensed_states,
                    credentials: form.credentials,
                    accepting_new_patients: form.accepting_new_patients,
                    timezone: form.timezone,
                    notification_mode: form.notification_mode,
                    notify_clinical_messages: form.notify_clinical_messages,
                    notify_appointments: form.notify_appointments,
                  });
                  setPanel(null);
                } catch (cause) {
                  setError(String(cause));
                }
              }}
              className="rounded bg-ink px-12 py-7 type-label-sm text-ink-onAccent disabled:opacity-40"
            >
              {update.isPending ? "Saving…" : "Save clinician"}
            </button>
            {error && <p className="type-body-sm text-danger">{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function RuleEditor({
  rule,
  clinicians,
  products,
  onChange,
  onRemove,
}: {
  rule: ClinicalAssignmentRule;
  clinicians: ClinicalTeamMember[];
  products: { product_id: string; label: string }[];
  onChange: (rule: ClinicalAssignmentRule) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const storefront = rule.channel === "storefront";
  const total = rule.members.reduce((sum, member) => sum + member.weight, 0);
  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return products
      .filter(
        (product) =>
          !query ||
          product.label.toLowerCase().includes(query) ||
          product.product_id.toLowerCase().includes(query),
      )
      .slice(0, 30);
  }, [products, search]);

  return (
    <div className="p-16">
      <div className="flex flex-wrap items-center justify-between gap-12">
        <div>
          <div className="flex flex-wrap items-center gap-7">
            <p className="type-body-sm font-medium">{rule.name}</p>
            <span className="rounded bg-sunken px-6 py-2 type-label-xs uppercase text-ink-muted">
              {storefront
                ? "Storefront orders"
                : rule.product_ids.length
                  ? `${rule.product_ids.length} products`
                  : "Global fallback"}
            </span>
            {!rule.active && (
              <span className="type-label-xs uppercase text-ink-muted">
                Inactive
              </span>
            )}
          </div>
          <p className="mt-4 type-label-xs text-ink-muted">
            {rule.members
              .map((member) => {
                const clinician = clinicians.find(
                  (item) => item.clinician_id === member.clinician_id,
                );
                const percentage = total
                  ? Math.round((member.weight / total) * 100)
                  : 0;
                return `${clinician?.display_name || clinician?.email || member.clinician_id} ${percentage}%`;
              })
              .join(" · ") || "No clinicians"}
          </p>
        </div>
        <div className="flex items-center gap-6">
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="rounded border border-line px-9 py-6 type-label-sm hover:bg-page"
          >
            {open ? "Close" : "Edit"}
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${rule.name}`}
            className="rounded border border-line p-7 text-danger hover:bg-danger/5"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-16 grid gap-16 border-t border-line-subtle pt-16 lg:grid-cols-2">
          <label className="type-label-sm">
            Rule name
            <input
              className={`${inputClass} mt-5`}
              value={rule.name}
              onChange={(event) =>
                onChange({ ...rule, name: event.target.value })
              }
            />
          </label>
          <label className="type-label-sm">
            Priority
            <input
              className={`${inputClass} mt-5`}
              type="number"
              min={0}
              value={rule.priority}
              onChange={(event) =>
                onChange({ ...rule, priority: Number(event.target.value) })
              }
            />
          </label>
          <label className="lg:col-span-2 inline-flex items-center gap-6 type-body-sm">
            <input
              type="checkbox"
              checked={rule.active}
              onChange={(event) =>
                onChange({ ...rule, active: event.target.checked })
              }
            />
            Active
          </label>

          {storefront ? (
            <div>
              <p className="type-label-sm">Product scope</p>
              <p className="mt-3 type-label-xs text-ink-muted">
                Every storefront order. Only these prescribers prescribe them,
                and storefront patients move to one of them.
              </p>
            </div>
          ) : (
            <div>
              <p className="type-label-sm">Product scope</p>
              <p className="mt-3 type-label-xs text-ink-muted">
                Leave empty for the global fallback.
              </p>
              <input
                className={`${inputClass} mt-8`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search treatments"
              />
              <div className="mt-8 max-h-[230px] overflow-auto rounded border border-line bg-page divide-y divide-line-subtle">
                {filteredProducts.map((product) => (
                  <label
                    key={product.product_id}
                    className="flex items-center gap-8 px-10 py-7 type-body-sm"
                  >
                    <input
                      type="checkbox"
                      checked={rule.product_ids.includes(product.product_id)}
                      onChange={(event) =>
                        onChange({
                          ...rule,
                          product_ids: event.target.checked
                            ? [...rule.product_ids, product.product_id]
                            : rule.product_ids.filter(
                                (id) => id !== product.product_id,
                              ),
                        })
                      }
                    />
                    <span className="truncate">{product.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="type-label-sm">Clinician proportions</p>
            <div className="mt-8 rounded border border-line bg-page divide-y divide-line-subtle">
              {clinicians
                .filter(
                  (member) =>
                    member.active && member.provider_type === "prescriber",
                )
                .map((clinician) => {
                  const member = rule.members.find(
                    (item) => item.clinician_id === clinician.clinician_id,
                  );
                  return (
                    <div
                      key={clinician.clinician_id}
                      className="grid grid-cols-[1fr_90px] items-center gap-10 px-10 py-8"
                    >
                      <label className="inline-flex items-center gap-7 type-body-sm min-w-0">
                        <input
                          type="checkbox"
                          checked={Boolean(member)}
                          onChange={(event) =>
                            onChange({
                              ...rule,
                              members: event.target.checked
                                ? [
                                    ...rule.members,
                                    {
                                      clinician_id: clinician.clinician_id,
                                      weight: 1,
                                    },
                                  ]
                                : rule.members.filter(
                                    (item) =>
                                      item.clinician_id !==
                                      clinician.clinician_id,
                                  ),
                            })
                          }
                        />
                        <span className="truncate">
                          {clinician.display_name || clinician.email}
                        </span>
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={1000}
                        disabled={!member}
                        aria-label={`Weight for ${clinician.display_name || clinician.email}`}
                        className="rounded border border-line bg-raised px-8 py-5 type-body-sm disabled:opacity-40"
                        value={member?.weight ?? 1}
                        onChange={(event) =>
                          onChange({
                            ...rule,
                            members: rule.members.map((item) =>
                              item.clinician_id === clinician.clinician_id
                                ? {
                                    ...item,
                                    weight: Math.max(
                                      1,
                                      Number(event.target.value),
                                    ),
                                  }
                                : item,
                            ),
                          })
                        }
                      />
                    </div>
                  );
                })}
              {!clinicians.some(
                (member) =>
                  member.active && member.provider_type === "prescriber",
              ) && (
                <p className="p-10 type-body-sm text-ink-muted">
                  Activate a prescriber above before building routing rules.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
